import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import SlidePanel from '../../shared/SlidePanel';
import toast from '../../shared/Toast';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import { prettyNumber, errorText, toE164, priceLine } from './api';
import { isOwnerHere } from '../../permissions/access';

/**
 * Bailey's phone. Numbers themselves live in Settings › Business numbers now
 * (Charlie, 2026-10-01): a location can have several, each with a job, and
 * the mobile one also does two-way texts. This panel:
 *
 *   no number      ->  why Bailey needs one, and Get a number (Settings,
 *                      which brings them back here after buying)
 *   has a number   ->  the number Bailey answers, a link to manage numbers,
 *                      and where calls go when someone asks for a person
 */
export default function PhonePanel({ fd, onClose }) {
    const s = fd.settings;
    const sheet = useRef(null);
    const alive = useRef(true);
    const history = useHistory();
    useEffect(() => () => { alive.current = false; }, []);
    const saved = toE164(s.voiceTransferNumber);
    const [transfer, setTransfer] = useState(saved);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [left, setLeft] = useState(false);       // left the field once: only then say it's short

    const transferOk = !transfer || isValidPhoneNumber(transfer);
    const transferChanged = (transfer || '') !== (saved || '');
    const has = !!s.voiceNumber;
    const here = `${window.location.pathname}${window.location.search}`;
    const toNumbers = (buy) => { hapticTick(); history.push(`/settings/business-numbers?${buy ? 'buy=1&' : ''}return=${encodeURIComponent(here)}`); };

    const close = () => { if (sheet.current) sheet.current.handleClose(); else onClose(); };
    const saveTransfer = async () => {
        if (!transferChanged || !transferOk) return;
        setBusy('save'); setError('');
        try {
            await fd.save({ voiceTransferNumber: transfer || '' });
            hapticSuccess();
            toast.success('Saved');
            if (alive.current) close();
        } catch (e) { if (!alive.current) return; hapticError(); setError(errorText(e, 'That didn’t save. Try again.')); setBusy(''); }
    };

    const footer = !has ? (
        <div className="fd-panel-footer">
            {/* Getting a number adds to the owner's plan: owner only. */}
            {isOwnerHere()
                ? <button type="button" className="sp-btn-primary" onClick={() => toNumbers(true)}>Get a number</button>
                : <span className="fd-hint">Ask the owner to get a number for this location.</span>}
        </div>
    ) : (
        // Nothing changed is not a dead end: the button closes the panel.
        <div className="fd-panel-footer">
            {transferChanged
                ? <button type="button" className="sp-btn-primary" disabled={!transferOk || busy === 'save'} onClick={saveTransfer}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
                : <button type="button" className="sp-btn-primary" onClick={() => { hapticTick(); close(); }}>Done</button>}
        </div>
    );

    return (
        <SlidePanel ref={sheet} title="Phone" onClose={onClose} width={480} className="bly-panel fd-form" footer={footer}>
            <div className="sp-form fd-phone">
                {error && <p className="sp-general-error" role="alert">{error}</p>}

                {!has && (
                    <>
                        <p className="fd-panel-lead">I need a phone number to answer calls. Get one in Settings › Business numbers: a mobile does calls and texts, so clients can reply to your texts too. Divert your salon line to it when you’re busy or closed, or give it out as it is.</p>
                        <p className="fd-price-note">£5 a month for the number. {priceLine(s)}</p>
                    </>
                )}

                {has && (
                    <>
                        <div className="fd-number-card">
                            <span className="sp-label">I answer</span>
                            <div className="fd-number">{prettyNumber(s.voiceNumber)}</div>
                            <p className="sp-hint">Divert your salon line here when you’re busy or closed; your phone provider can set it up in a couple of minutes. You can also give this number out as it is.</p>
                            <p className="sp-hint">{priceLine(s)}</p>
                            <button type="button" className="fd-give-back" onClick={() => toNumbers(false)}>Manage numbers in Settings</button>
                        </div>
                        <div className="sp-field">
                            <label className="sp-label" htmlFor="fd-transfer">When someone asks for a person, put them through to</label>
                            <div className="fd-phone-input">
                                <PhoneInput id="fd-transfer" international defaultCountry="GB" countryCallingCodeEditable={false} placeholder="Salon phone number" value={transfer || undefined} onChange={(v) => setTransfer(v || '')} onBlur={() => setLeft(true)} />
                            </div>
                            {/* Only once they've changed it and moved on: a saved number never opens with an error, and typing isn't told off. */}
                            {left && transferChanged && transfer && !transferOk
                                ? <p className="sp-error">That doesn’t look like a full phone number yet.</p>
                                : <p className="sp-hint">Leave it empty and I take a message instead. It comes to you in Needs you.</p>}
                        </div>
                    </>
                )}
            </div>
        </SlidePanel>
    );
}
